import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogTitle,
  DialogContent,
  Dialog,
  IconButton,
  DialogActions,
  Typography,
  Box,
  TableContainer,
  TableHead,
  Table,
  TableCell,
  TableRow,
  TableBody,
  useTheme,
  LinearProgress,
  ButtonBase,
  TablePagination,
  Chip,
  Stack,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import {
  useNavigate, useParams, useLocation,
} from 'react-router-dom';
import { IconArrowRight } from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate } from 'src/utils';
import { assignDistributor, fetchActiveDistributors } from 'src/store/actions/users.action';
import { toast } from 'react-toastify';
import config from '../../config';
import EmptyComponent from '../EmptyComponent';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

function ActiveDistributorsTable({ users, loading }) {
  const globalTheme = useTheme();
  const navigate = useNavigate();
  const { palette } = globalTheme;
  const dispatch = useDispatch();
  const { id } = useParams();

  const { roles, userStatuses } = useSelector((state) => state.app);
  const { assignDistributorStatus } = useSelector((st) => st.users);

  const [limit, setLimit] = useState(4);
  const [page, setPage] = useState(0);

  const headers = [
    { id: 1, label: 'Distributor', key: 'user' },
    { id: 2, label: 'Phone Number', key: 'phone' },
    { id: 3, label: 'Email', key: 'email' },
    { id: 6, label: 'Action', key: 'action' },
  ];

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const assignAgentToDistributor = async (item) => {
    try {
      const payload = { salesUserId: id, distributorId: item.id };
      const response = await dispatch(assignDistributor(payload)).unwrap();

      if (response) {
        toast.success('Distributor assigned');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <>
      {(loading || assignDistributorStatus === 'loading') && (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
      )}

      <TableContainer sx={{
        borderWidth: '1px',
        borderColor: palette.grey[300],
        borderStyle: 'solid',
        borderRadius: '16px',
      }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {headers.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                >
                  <Typography sx={{ fontWeight: 'normal' }}>{column.label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {!loading && users && users.length === 0 && (
          <TableBody>
            <TableRow style={{ height: 300 }}>
              <TableCell colSpan={12}>
                <EmptyComponent />
              </TableCell>
            </TableRow>
          </TableBody>
          )}

          {users && users.length > 0 && (
          <TableBody sx={{ }}>

            {(limit > 0
              ? users.slice(page * limit, page * limit + limit)
              : users
            ).map((item) => (

              <TableRow
                hover
                key={item.id}
              >
                <TableCell sx={{}}>
                  <Stack direction="row" alignContent="center" alignItems="center">
                    <Avatar alt="Profile pic" src={item.profilePicture} />
                    <Typography sx={{ ml: 1 }}>
                      {item.firstName}
                      {' '}
                      {item.lastName}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>{item.phone}</TableCell>
                <TableCell>{item.email}</TableCell>

                <TableCell>
                  <ButtonBase
                    sx={{
                      borderRadius: '30px',
                      px: 3,
                      py: 1,
                      backgroundColor: palette.secondary.light,
                    }}
                    disabled={assignDistributorStatus === 'loading'}
                    onClick={() => assignAgentToDistributor(item)}
                  >
                    <Typography color="white">Assign</Typography>
                  </ButtonBase>

                </TableCell>

              </TableRow>
            ))}
          </TableBody>
          )}
        </Table>
      </TableContainer>

      {users && users.length > 0 && (
        <TablePagination
          component="div"
          count={users.length}
          page={page}
          colSpan={3}
          rowsPerPage={limit}
          rowsPerPageOptions={[4, 8, 12, { label: 'All', value: -1 }]}
          SelectProps={{
            inputProps: {
              'aria-label': 'users per page',
            },
            native: true,
          }}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
        />
      )}
    </>
  );
}

ActiveDistributorsTable.propTypes = {
  users: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default function AssignDistributorDialog({ open, setOpen }) {
  const dispatch = useDispatch();
  const { id } = useParams();

  const [activeDistributors, setActiveDistributors] = useState([]);

  const fetchDistributors = async () => {
    try {
      const response = await dispatch(fetchActiveDistributors()).unwrap();
      setActiveDistributors(response.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchDistributors();
  }, [dispatch]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box>

      <BootstrapDialog
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
          Distributors
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <Box sx={{ p: 4 }}>
            <ActiveDistributorsTable loading={false} users={activeDistributors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Done
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </Box>
  );
}
